import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";

const SUPPORT_EMAIL = "support@creaiops.com";

const SECTIONS = [
  {
    id: "privacy",
    title: "1. Privacy Notice",
    paragraphs: [
      "CRE should be used with anonymized incident information only. Users must not enter passwords, secrets, API keys, personal data, customer data, regulated data, confidential production logs, or commercially sensitive information.",
      "CRE may process user-submitted information to generate AI-assisted incident reasoning outputs, including likely RCA hypotheses, recommended actions, stakeholder updates, and human-reviewable runbooks.",
      "CRE may store account information, plan details, usage history, submitted incident scenarios, and analysis results based on the active plan. Analysis history may be retained for a limited period to support user access and product improvement.",
      "CRE may use third-party infrastructure, hosting, payment, authentication, analytics, and AI model services to operate the platform. Data may be processed by these services only to provide the CRE service.",
      "Users are responsible for ensuring that information submitted into CRE is anonymized and suitable for AI-assisted processing.",
      "Where applicable, users may contact CRE to request access, correction, or deletion of their account or stored analysis information.",
    ],
    contact: SUPPORT_EMAIL,
  },
  {
    id: "terms",
    title: "2. Site Terms",
    paragraphs: [
      "CRE provides advisory AI-generated analysis only.",
      "CRE outputs may be incomplete, incorrect, delayed, or not suitable for a specific production environment. All outputs must be reviewed and validated by qualified human operators before any operational decision is taken.",
      "CRE does not replace ITSM platforms, observability tools, security tools, compliance processes, change management, incident governance, or professional operational judgement.",
      "CRE does not execute remediation actions automatically. Human approval and validation are mandatory before taking any action.",
      "Users remain responsible for decisions, actions, changes, communications, and operational outcomes based on CRE outputs.",
      "CRE may evolve as the platform improves and new capabilities are introduced.",
    ],
  },
  {
    id: "security",
    title: "3. Security",
    paragraphs: [
      "CRE currently works with anonymized user-provided incident scenarios.",
      "Users must not enter secrets, credentials, API keys, tokens, passwords, customer records, confidential logs, regulated data, or sensitive production data.",
      "CRE does not directly connect to or change production systems unless explicitly enabled through approved future integrations.",
      "CRE does not automatically execute scripts, commands, remediation steps, or infrastructure changes.",
      "Human-in-the-loop approval is required for any recommended action.",
    ],
    contactLabel: "Security concerns can be reported to",
    contact: SUPPORT_EMAIL,
  },
  {
    id: "accessibility",
    title: "4. Accessibility",
    paragraphs: [
      "CRE aims to provide a usable and accessible experience across modern browsers and devices.",
      "If users face accessibility issues or need support accessing any part of the platform, they can contact CRE.",
    ],
    contact: SUPPORT_EMAIL,
  },
  {
    id: "contact",
    title: "5. Contact",
    paragraphs: [
      "For feedback, support, investor queries, product questions, accessibility issues, or security concerns, contact:",
    ],
    contact: SUPPORT_EMAIL,
  },
];

export default function Trust() {
  const { hash } = useLocation();

  // Scroll to the requested anchor (#privacy, #terms, ...) on mount/hash change.
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    const id = hash.replace("#", "");
    // Defer to next tick so the section is in the DOM.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Header />

      <main
        data-testid="trust-page"
        className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-12 md:pb-16"
      >
        {/* Back to home */}
        <Link
          to="/"
          data-testid="trust-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          Back to Home
        </Link>

        <div className="mt-5">
          <div
            data-testid="trust-pill"
            className="cre-pill cre-pill-grad"
          >
            <ShieldCheck className="h-3 w-3" strokeWidth={2} />
            Trust, Privacy &amp; Terms
          </div>

          <h1
            data-testid="trust-heading"
            className="mt-4 font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.08]"
          >
            CRE Trust, Privacy &amp; Terms
          </h1>

          <p
            data-testid="trust-intro"
            className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed"
          >
            CRE — Collaborative Resolution Environment is an AI incident
            reasoning workspace for IT operations. This page explains the
            privacy, usage, security, accessibility, and contact position for
            the CRE platform.
          </p>
        </div>

        {/* Quick jump nav */}
        <nav
          data-testid="trust-toc"
          aria-label="Trust page sections"
          className="mt-7 cre-card p-3 md:p-4"
        >
          <div className="cre-overline text-slate-500 mb-2">On this page</div>
          <ul className="flex flex-wrap gap-x-3 gap-y-1.5 text-[13px]">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`trust-toc-${s.id}`}
                  className="text-slate-600 hover:text-sky-700 transition-colors"
                >
                  {s.title.replace(/^\d+\.\s*/, "")}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-8 space-y-10 md:space-y-12">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={s.id}
              data-testid={`trust-section-${s.id}`}
              className="scroll-mt-24"
            >
              <h2 className="font-heading font-bold tracking-tight text-xl md:text-2xl text-slate-900">
                {s.title}
              </h2>
              <div className="mt-3 space-y-3 text-sm sm:text-[15px] text-slate-700 leading-relaxed">
                {s.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                {s.contact ? (
                  <p
                    data-testid={`trust-${s.id}-contact`}
                    className="flex flex-wrap items-center gap-1.5 pt-1"
                  >
                    <Mail className="h-3.5 w-3.5 text-sky-600" strokeWidth={2} />
                    <span className="text-slate-600">
                      {s.contactLabel || "Contact"}:
                    </span>
                    <a
                      href={`mailto:${s.contact}`}
                      className="text-sky-700 hover:text-sky-800 underline-offset-4 hover:underline font-medium"
                    >
                      {s.contact}
                    </a>
                  </p>
                ) : null}
              </div>
            </section>
          ))}
        </div>

        {/* Bottom back-to-home */}
        <div className="mt-12 pt-6 border-t border-slate-200">
          <Link to="/" data-testid="trust-back-home-bottom">
            <Button
              variant="outline"
              className="rounded-lg border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-semibold"
            >
              <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={2} />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
